import { useNavigate } from "react-router-dom";
import {
  ChevronRight, Gift, HelpCircle, ShieldCheck, ClipboardList, Smartphone, Camera,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useState, useEffect } from "react";
import InstallGuideModal, { canShowInstallGuide } from "../components/InstallGuideModal";

export default function More() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showInstall, setShowInstall] = useState(false);
  const [installEligible, setInstallEligible] = useState(false);

  useEffect(() => {
    const recompute = () => setInstallEligible(canShowInstallGuide());
    recompute();
    window.addEventListener("focus", recompute);
    return () => window.removeEventListener("focus", recompute);
  }, []);

  if (!user) return null;
  const isStaff = user.role === "admin" || user.role === "reviewer";

  const items = [
    {
      key: "refer", to: "/refer",
      icon: <Gift size={20} color="#FFFFFF" />,
      bg: "linear-gradient(135deg, #E63B5A, #C2284A)",
      title: "Refer a Friend",
      sub: "Earn up to £5 when their first receipt is approved",
      testid: "more-refer",
    },
    {
      key: "upload", to: "/upload",
      icon: <Camera size={20} color="#FFFFFF" />,
      bg: "linear-gradient(135deg, #F77A2A, #E25C12)",
      title: "Upload Receipt",
      sub: "Earn up to £5 per approved receipt",
      testid: "more-upload",
    },
    {
      key: "surveys", to: "/surveys",
      icon: <ClipboardList size={20} color="#FFFFFF" />,
      bg: "linear-gradient(135deg, #2EBFA6, #1F9C87)",
      title: "Earn on Surveys",
      sub: "Share your opinions",
      testid: "more-surveys",
    },
    {
      key: "how", to: "/how-it-works",
      icon: <HelpCircle size={20} color="#FFFFFF" />,
      bg: "var(--navy)",
      title: "How it Works",
      sub: "Quick guide to earning cashback",
      testid: "more-how",
    },
  ];
  if (isStaff) {
    items.push({
      key: "admin", to: "/admin",
      icon: <ShieldCheck size={20} color="#FFFFFF" />,
      bg: "var(--navy)",
      title: user.role === "reviewer" ? "Reviewer Panel" : "Admin Panel",
      sub: user.role === "reviewer" ? "Review receipts" : "Manage receipts, cashouts, users",
      testid: "more-admin",
    });
  }

  return (
    <div className="page fade-in" data-testid="more-page">
      <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: -1, margin: 0, marginBottom: 6 }}>
        More
      </h1>
      <p style={{ color: "var(--text-secondary)", fontSize: 14, marginBottom: 22 }}>
        Bonuses, surveys, and tools.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map((it) => (
          <button
            key={it.key}
            data-testid={it.testid}
            onClick={() => navigate(it.to)}
            style={{
              display: "flex", alignItems: "center", gap: 14, textAlign: "left",
              background: "var(--surface)", border: "1px solid var(--border)",
              borderRadius: 22, padding: 14, width: "100%",
            }}
          >
            <span style={{
              width: 44, height: 44, borderRadius: 14,
              background: it.bg,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 10px 22px -14px rgba(11,36,71,0.4)",
            }}>
              {it.icon}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>{it.title}</div>
              <div style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 2 }}>{it.sub}</div>
            </span>
            <ChevronRight size={20} color="var(--text-muted)" />
          </button>
        ))}

        {installEligible && (
          <button
            data-testid="more-install-app"
            onClick={() => setShowInstall(true)}
            style={{
              display: "flex", alignItems: "center", gap: 14, textAlign: "left",
              background: "var(--surface)", border: "1px solid var(--border)",
              borderRadius: 22, padding: 14, width: "100%",
            }}
          >
            <span style={{
              width: 44, height: 44, borderRadius: 14, background: "var(--navy)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Smartphone size={20} color="#FFFFFF" />
            </span>
            <span style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>Install App</div>
              <div style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 2 }}>Add to home screen for one-tap access</div>
            </span>
            <ChevronRight size={20} color="var(--text-muted)" />
          </button>
        )}
      </div>

      <InstallGuideModal open={showInstall} onClose={() => setShowInstall(false)} />
    </div>
  );
}
